import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQueryClient } from 'react-query'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar'
import { CoachLogic } from '../../components/ui/coach-logic'
import { InsightFeedbackDialog } from '../../components/ui/insight-feedback-dialog'
import { ErrorBoundary } from '../../components/ui/error-boundary'
import { getRoleFromPath } from '../../utils/helper'
import { TrendingUp, TrendingDown, ThumbsUp, ThumbsDown, Minus, BarChart3 } from 'lucide-react'
import {
  useGetInsightsQuery,
  useUpdateInsightMutation,
  useGetBusinessProfileQuery,
} from '../../__generated__/api-types-and-hooks'
import { RootState } from '../../App'
import { useFeatureFlag } from 'hooks/userFeatureFlag'

interface InsightsProps {
  clientId?: string // If provided, renders in advisor mode viewing client's insights
}

const Insights: React.FC<InsightsProps> = ({ clientId }) => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const history = useHistory()
  const isAdvisorView = !!clientId

  // Get userId - either from Redux (owner view) or from prop (advisor view)
  const userIdFromRedux = useSelector((state: RootState) => state.user.user.id)
  const userId = isAdvisorView ? clientId : userIdFromRedux

  // Get business name - either from Redux (owner view) or from API (advisor view)
  const businessNameFromRedux = useSelector(
    (state: RootState) => state.user.businessProfile?.businessName || 'Your Business'
  )
  const { data: businessData } = useGetBusinessProfileQuery(
    { id: clientId! },
    { enabled: isAdvisorView, refetchOnWindowFocus: false }
  )
  const businessName = isAdvisorView
    ? businessData?.getBusinessProfile?.businessName || "Your Client's Business"
    : businessNameFromRedux

  const isPerformanceAnalyticsEnabled = useFeatureFlag(userId, 'release-performance-analytics')

  const queryClient = useQueryClient()

  // Fetch insights from API
  const {
    data: insightsData,
    isLoading,
    isError,
    refetch: refetchInsights,
  } = useGetInsightsQuery({ tenantId, userId }, { refetchOnWindowFocus: false })

  // Mutation for updating insights
  const updateInsightMutation = useUpdateInsightMutation()

  // State for tracking feedback on insights
  const [feedback, setFeedback] = useState<Record<string, 'helpful' | 'not-helpful' | null>>({})
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [currentInsight, setCurrentInsight] = useState<{
    id: string
    title: string
    description?: string
    why?: string
    priority?: string
    session?: string
  } | null>(null)

  // Function to mark insight as viewed (only in owner view)
  const markInsightAsViewed = async (insightId: string) => {
    // Only mark as viewed in owner view, not in advisor view
    if (isAdvisorView) return

    try {
      await updateInsightMutation.mutateAsync({
        input: {
          tenantId,
          userId,
          insightId,
          hasBeenViewed: true,
        },
      })
      // Refetch insights to update the list
      refetchInsights()
      // Invalidate the unread insights count query to update the counter
      queryClient.invalidateQueries(['GetInsights', { tenantId, userId, hasBeenViewed: false }])
    } catch (error) {
      console.error('Failed to update insight:', error)
    }
  }

  // Function to handle feedback clicks
  const handleFeedback = (insightId: string, feedbackType: 'helpful' | 'not-helpful') => {
    // Mark insight as viewed when user interacts with it (only in owner view)
    markInsightAsViewed(insightId)

    if (feedbackType === 'helpful') {
      // Like button: Set isApproved to true (async, non-blocking)
      setFeedback((prev) => ({
        ...prev,
        [insightId]: 'helpful',
      }))

      // Update in background - don't await
      updateInsightMutation
        .mutateAsync({
          input: {
            tenantId,
            userId,
            insightId,
            isApproved: true,
          },
        })
        .then(() => {
          refetchInsights()
        })
        .catch((error) => {
          console.error('Failed to approve insight:', error)
        })
    } else {
      // Dislike button: Open dialog immediately, update async
      const insight = insightsData?.getInsights?.items?.find((i) => i.insightId === insightId)
      if (insight) {
        // Update local state and open dialog immediately
        setFeedback((prev) => ({
          ...prev,
          [insightId]: 'not-helpful',
        }))

        setCurrentInsight({
          id: insight.insightId,
          title: insight.name,
          description: insight.description || undefined,
          why: insight.why || undefined,
          priority: insight.priority || undefined,
          session: insight.session || undefined,
        })
        setFeedbackDialogOpen(true)

        // Update isApproved in background - don't await
        updateInsightMutation
          .mutateAsync({
            input: {
              tenantId,
              userId,
              insightId,
              isApproved: false,
            },
          })
          .then(() => {
            refetchInsights()
          })
          .catch((error) => {
            console.error('Failed to mark insight as not helpful:', error)
          })
      }
    }
  }

  // Function to handle session creation from the dialog
  const handleSessionCreated = async (sessionId: string) => {
    if (currentInsight) {
      try {
        await updateInsightMutation.mutateAsync({
          input: {
            tenantId,
            userId,
            insightId: currentInsight.id,
            session: sessionId,
          },
        })
      } catch (error) {
        console.error('Failed to update session:', error)
      }
    }
  }

  // Function to handle feedback dialog submission
  const handleFeedbackSubmit = (feedbackText: string) => {
    if (currentInsight) {
      setFeedback((prev) => ({
        ...prev,
        [currentInsight.id]: 'not-helpful',
      }))
    }
    setCurrentInsight(null)
  }

  // Function to handle tackle it navigation
  const handleTackleIt = (insightId: string) => {
    // Mark insight as viewed when user opens it (only in owner view)
    markInsightAsViewed(insightId)

    const roleFromPath = getRoleFromPath()
    const basePath = isAdvisorView
      ? `/${roleFromPath}/${tenantId}/clients/${clientId}/insights/${insightId}`
      : `/${roleFromPath}/${tenantId}/insights/${insightId}`
    history.push(basePath)
  }

  // Transform API data to match component structure
  const insights =
    insightsData?.getInsights?.items?.map((insight) => {
      // Map priority enum to lowercase string
      const priorityMap: Record<string, 'high' | 'medium' | 'low'> = {
        HIGH_PRIORITY: 'high',
        MEDIUM_PRIORITY: 'medium',
        LOW_PRIORITY: 'low',
      }
      const priority = insight.priority ? priorityMap[insight.priority] || 'medium' : 'medium'

      return {
        id: insight.insightId || '',
        title: insight.name || '',
        description: insight.description || '',
        category: insight.category?.toLowerCase() || '',
        priority,
        businessFunction: insight.category?.toLowerCase() || '',
        why: insight.why || '',
        actionableDirectives: {
          doMore: [],
          changeStop: [],
          start: [],
        },
        metrics:
          insight.metrics?.map((metric) => {
            // Map trend values from API to component values
            const trendMap: Record<string, 'up' | 'down' | 'neutral'> = {
              UP: 'up',
              DOWN: 'down',
              NEUTRAL: 'neutral',
            }
            const trend = metric?.trend ? trendMap[metric.trend] || 'neutral' : 'neutral'

            return {
              name: metric?.metricTitle || '',
              value: metric?.current_status?.toString() || '',
              trend,
              benchmark: metric?.benchmark?.toString() || '',
            }
          }) || [],
        plays:
          insight.playsId?.map((playId, index) => ({
            id: playId || '',
            title: `Play ${index + 1}`,
            timeEstimate: '1-2 weeks',
            impact: 'medium' as const,
            priority: 'medium' as const,
            category: (insight.category?.toLowerCase() || 'do-more') as
              | 'do-more'
              | 'change'
              | 'start',
          })) || [],
        goals:
          insight.goalsId?.map((goalId, index) => ({
            id: goalId || '',
            title: `Goal ${index + 1}`,
            deadline: '3 months',
            progress: 0,
            priority,
          })) || [],
        // Include approval state and session from API
        isApproved: insight.isApproved ?? null,
        session: insight.session ?? null,
      }
    }) || []

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-[#FDE8E8] text-[#B91C1C] border-[#FECACA]'
      case 'medium':
        return 'bg-[#FFF7E6] text-[#D97706] border-[#FDE68A]' // Updated styling for medium priority
      case 'low':
        return 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]'
      default:
        return 'bg-gray-100 text-gray-700 border-0'
    }
  }

  const getBusinessFunctionBadgeClass = (businessFunction: string) => {
    switch (businessFunction) {
      case 'marketing':
        return 'bg-[#F3E8FF] text-[#7E22CE] border-[#E9D5FF]'
      case 'operations':
        return 'bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]'
      case 'sales':
        return 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]'
      case 'finance':
        return 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]'
      default:
        return 'bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]'
    }
  }

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header Section */}
      <div className="mb-2 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Business Insights</h1>
            <p className="mt-1 text-sm" style={{ color: '#6B7280' }}>
              Actionable recommendations based on{' '}
              {isAdvisorView ? `${businessName}'s` : 'your business'} metrics
            </p>
          </div>
          <div className="flex gap-2">
            {isPerformanceAnalyticsEnabled && (
              <Button
                variant="outline"
                className="gap-2"
                style={{
                  backgroundColor: 'white',
                  borderColor: '#E5E7EB',
                  color: '#374151',
                  borderWidth: '1px',
                }}
              >
                <BarChart3 className="h-4 w-4" style={{ color: '#374151' }} />
                Performance Analytics
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div className="px-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#705AF8]"></div>
              <p className="mt-4 text-gray-600">Loading insights...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-800">Failed to load insights. Please try refreshing the page.</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && insights.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
            <p className="text-gray-600 text-lg">No insights available at the moment.</p>
            <p className="text-gray-500 mt-2">
              Check back later for personalized recommendations based on{' '}
              {isAdvisorView ? 'business' : 'your business'} data.
            </p>
          </div>
        )}

        {/* Insights Content */}
        {!isLoading && !isError && insights.length > 0 && (
          <>
            {/* Coach Logic Section */}
            <CoachLogic
              message={`Welcome back to ${
                isAdvisorView ? "your client's" : 'your'
              } insights! I've analyzed ${businessName}'s data and found ${
                insights.length
              } key opportunities that could help ${
                isAdvisorView ? 'their' : 'your'
              } business grow.${
                insights.some((i) => i.priority === 'high')
                  ? ' The high-priority insights need your attention first.'
                  : ''
              }`}
              suggestions={[
                'Tell me more about these insights',
                'Show me all high-priority insights',
                'What should I focus on today?',
              ]}
              className="mb-6"
            />

            {/* Dynamic Insight Cards */}
            <div className="space-y-6">
              {insights.map((insight) => (
                <div
                  key={insight.id}
                  className="bg-white rounded-lg overflow-hidden"
                  style={{
                    border: '1px solid #E5E7EB',
                    borderTop: `4px solid ${
                      insight.priority === 'high'
                        ? '#EF4444'
                        : insight.priority === 'medium'
                        ? '#FBBF24'
                        : '#10B981'
                    }`, // Adjusted medium border color
                  }}
                >
                  {' '}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <h2
                          className="text-lg font-semibold text-[#111827] mb-2 cursor-pointer hover:underline"
                          onClick={() => handleTackleIt(insight.id)}
                        >
                          {insight.title}
                        </h2>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {insight.description}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4 flex-shrink-0">
                        <Badge
                          variant="outline"
                          className={`${getPriorityBadgeClass(
                            insight.priority
                          )} rounded-md px-3 py-1 text-xs font-medium shadow-sm`}
                        >
                          {insight.priority.charAt(0).toUpperCase() + insight.priority.slice(1)}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`${getBusinessFunctionBadgeClass(
                            insight.businessFunction
                          )} rounded-md px-3 py-1 text-xs font-medium shadow-sm`}
                        >
                          {insight.businessFunction.charAt(0).toUpperCase() +
                            insight.businessFunction.slice(1)}
                        </Badge>
                      </div>
                    </div>

                    {/* Related Metrics */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Related Metrics</span>
                      </div>
                      <div className="flex gap-16">
                        {insight.metrics.map((metric, index) => (
                          <div key={index} className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-900">{metric.name}</span>
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  metric.trend === 'up'
                                    ? 'bg-green-400'
                                    : metric.trend === 'down'
                                    ? 'bg-red-400'
                                    : 'bg-gray-400'
                                }`}
                              >
                                {metric.trend === 'up' ? (
                                  <TrendingUp
                                    className="h-4 w-4"
                                    style={{ stroke: 'green', fill: 'none' }}
                                  />
                                ) : metric.trend === 'down' ? (
                                  <TrendingDown
                                    className="h-4 w-4"
                                    style={{ stroke: 'red', fill: 'none' }}
                                  />
                                ) : (
                                  <Minus
                                    className="h-4 w-4"
                                    style={{ stroke: '#374151', fill: 'none' }}
                                  />
                                )}
                              </div>
                            </div>
                            <div className="flex items-baseline justify-between">
                              <p className="text-xl font-semibold text-gray-900">{metric.value}</p>
                              <p className="text-sm text-gray-500">Benchmark: {metric.benchmark}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Why It Matters */}
                    <div className="mb-6">
                      <div className="flex items-center mb-2">
                        <span
                          className="text-sm font-medium flex items-center"
                          style={{ color: '#111827' }}
                        >
                          <span style={{ marginRight: '6px' }}>💡</span>
                          Why It Matters
                        </span>
                      </div>

                      <div
                        className="rounded-lg p-4"
                        style={{
                          backgroundColor: '#EFF6FF', // soft blue background (v0 style)
                          border: '1px solid #BFDBFE', // subtle blue border
                        }}
                      >
                        <div className="flex items-start gap-3">
                          {/* Avatar */}
                          <Avatar
                            style={{
                              height: '2rem',
                              width: '2rem',
                              // border: '2px solid #3B82F6',
                            }}
                          >
                            <AvatarImage src="/images/coach-logic-avatar.png" alt="Coach Logic" />
                            <AvatarFallback
                              style={{
                                backgroundColor: '#3B82F6',
                                color: 'white',
                              }}
                            >
                              CL
                            </AvatarFallback>
                          </Avatar>

                          {/* Text content */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-medium" style={{ color: '#1E40AF' }}>
                                Coach Logic
                              </span>
                              <Badge
                                variant="outline"
                                className="text-xs px-2 py-[2px] rounded border"
                                style={{
                                  backgroundColor: '#DBEAFE',
                                  color: '#1D4ED8',
                                  borderColor: '#93C5FD',
                                }}
                              >
                                Insight
                              </Badge>
                            </div>

                            <p className="text-sm leading-relaxed" style={{ color: '#1E3A8A' }}>
                              Hey there! Let me break this down for you. {insight.why} This is
                              exactly the kind of opportunity that can make a real difference in
                              {isAdvisorView ? ` ${businessName}'s` : ' your business'} performance.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feedback Section */}
                    <div
                      className="flex items-center justify-between pt-2"
                      // style={{ borderTop: '1px solid #E5E7EB' }}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600">Is this insight helpful?</span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            style={{
                              height: '1.75rem',
                              padding: '0 0.5rem',
                              backgroundColor:
                                feedback[insight.id] === 'helpful' ||
                                (feedback[insight.id] === undefined && insight.isApproved === true)
                                  ? '#f0fdf4'
                                  : 'transparent',
                              color:
                                feedback[insight.id] === 'helpful' ||
                                (feedback[insight.id] === undefined && insight.isApproved === true)
                                  ? '#15803d'
                                  : '#374151',
                            }}
                            onClick={() => handleFeedback(insight.id, 'helpful')}
                          >
                            <ThumbsUp
                              style={{
                                width: '1rem',
                                height: '1rem',
                                marginRight: '0.25rem',
                                stroke:
                                  feedback[insight.id] === 'helpful' ||
                                  (feedback[insight.id] === undefined &&
                                    insight.isApproved === true)
                                    ? '#15803d'
                                    : '#374151',
                                fill: 'none',
                              }}
                            />
                            Yes
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            style={{
                              height: '1.75rem',
                              padding: '0 0.5rem',
                              backgroundColor:
                                feedback[insight.id] === 'not-helpful' ||
                                (feedback[insight.id] === undefined && insight.isApproved === false)
                                  ? '#fef2f2'
                                  : 'transparent',
                              color:
                                feedback[insight.id] === 'not-helpful' ||
                                (feedback[insight.id] === undefined && insight.isApproved === false)
                                  ? '#b91c1c'
                                  : '#374151',
                            }}
                            onClick={() => handleFeedback(insight.id, 'not-helpful')}
                          >
                            <ThumbsDown
                              style={{
                                width: '1rem',
                                height: '1rem',
                                marginRight: '0.25rem',
                                stroke:
                                  feedback[insight.id] === 'not-helpful' ||
                                  (feedback[insight.id] === undefined &&
                                    insight.isApproved === false)
                                    ? '#b91c1c'
                                    : '#374151',
                                fill: 'none',
                              }}
                            />
                            No
                          </Button>
                        </div>
                      </div>
                      <Button
                        className="bg-[#705AF8] hover:bg-[#705AF8]/90 text-white text-sm font-medium px-6"
                        onClick={() => handleTackleIt(insight.id)}
                      >
                        Tackle it
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Feedback Dialog */}
      <ErrorBoundary
        onReset={() => {
          setFeedbackDialogOpen(false)
          setCurrentInsight(null)
        }}
      >
        <InsightFeedbackDialog
          isOpen={feedbackDialogOpen}
          onClose={() => {
            setFeedbackDialogOpen(false)
            setCurrentInsight(null)
          }}
          onSubmit={handleFeedbackSubmit}
          itemTitle={currentInsight?.title || ''}
          itemDescription={currentInsight?.description}
          itemId={currentInsight?.id}
          insightContext={{
            why: currentInsight?.why,
            priority: currentInsight?.priority,
          }}
          existingSessionId={currentInsight?.session}
          onSessionCreated={handleSessionCreated}
        />
      </ErrorBoundary>
    </div>
  )
}

export default Insights
